/* Permanent Hiring — target/search animation */
export function PermanentHiringIllustration() {
  return (
    <div className="relative h-44 bg-[#060E1A] flex items-center justify-center overflow-hidden">
      {/* Dot-grid background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(232,80,10,0.22) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />
      <svg width="180" height="140" viewBox="0 0 180 140" className="relative z-10" aria-hidden="true">
        {/* Pulsing concentric rings */}
        <circle cx="90" cy="68" r="42" stroke="rgba(232,80,10,0.35)" strokeWidth="1" fill="none" className="ring-pulse-1" />
        <circle cx="90" cy="68" r="42" stroke="rgba(232,80,10,0.2)" strokeWidth="1" fill="none" className="ring-pulse-2" />
        {/* Person head */}
        <circle cx="90" cy="52" r="16" fill="rgba(232,80,10,0.12)" stroke="#E8500A" strokeWidth="1.8" />
        {/* Person body */}
        <path d="M60 102 C60 84 72 76 90 76 C108 76 120 84 120 102" fill="rgba(232,80,10,0.08)" stroke="#E8500A" strokeWidth="1.8" strokeLinecap="round" />
        {/* Check badge */}
        <circle cx="124" cy="90" r="14" fill="#E8500A" />
        <path d="M118 90 L122 94.5 L131 83" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

/* Contract Staffing — 3-node network / flexibility model */
export function ContractStaffingIllustration() {
  return (
    <div className="relative h-44 bg-[#060E1A] flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(rgba(232,80,10,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(232,80,10,0.08) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />
      <svg width="180" height="140" viewBox="0 0 180 140" className="relative z-10" aria-hidden="true">
        {/* Top node — W2 */}
        <circle cx="90" cy="28" r="14" fill="rgba(232,80,10,0.14)" stroke="#E8500A" strokeWidth="1.8" />
        <circle cx="90" cy="24" r="5.5" fill="#E8500A" />
        <text x="90" y="49" textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="7.5" fontFamily="Inter,sans-serif" fontWeight="700" letterSpacing="0.5">W2</text>
        {/* Bottom-left node — C2C */}
        <circle cx="42" cy="104" r="13" fill="rgba(232,80,10,0.1)" stroke="rgba(232,80,10,0.65)" strokeWidth="1.6" />
        <circle cx="42" cy="100" r="5" fill="rgba(232,80,10,0.7)" />
        <text x="42" y="124" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="Inter,sans-serif" fontWeight="600">C2C</text>
        {/* Bottom-right node — Flex */}
        <circle cx="138" cy="104" r="13" fill="rgba(232,80,10,0.1)" stroke="rgba(232,80,10,0.65)" strokeWidth="1.6" />
        <circle cx="138" cy="100" r="5" fill="rgba(232,80,10,0.7)" />
        <text x="138" y="124" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="Inter,sans-serif" fontWeight="600">Flex</text>
        {/* Dashed connectors */}
        <line x1="84" y1="42" x2="50" y2="93" stroke="rgba(232,80,10,0.3)" strokeWidth="1.2" strokeDasharray="4,3" />
        <line x1="96" y1="42" x2="130" y2="93" stroke="rgba(232,80,10,0.3)" strokeWidth="1.2" strokeDasharray="4,3" />
        <line x1="55" y1="106" x2="125" y2="106" stroke="rgba(232,80,10,0.2)" strokeWidth="1" strokeDasharray="4,3" />
        {/* Rotating arrows */}
        <g className="spin-slow" style={{ transformOrigin: '90px 70px', transformBox: 'view-box' }}>
          <path d="M80 65 A13 13 0 0 1 100 65" stroke="rgba(232,80,10,0.95)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path d="M99 65 L103 61 M99 65 L103 69" stroke="rgba(232,80,10,0.95)" strokeWidth="2" strokeLinecap="round" />
          <path d="M100 75 A13 13 0 0 1 80 75" stroke="rgba(232,80,10,0.95)" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <path d="M81 75 L77 71 M81 75 L77 79" stroke="rgba(232,80,10,0.95)" strokeWidth="2" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

/* Executive Search — magnifying glass with star + scan sweep */
export function ExecutiveSearchIllustration() {
  return (
    <div className="relative h-44 bg-[#060E1A] flex items-center justify-center overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-[#E8500A]/8 rounded-full blur-3xl" />
      <svg width="180" height="140" viewBox="0 0 180 140" className="relative z-10" aria-hidden="true">
        {/* Lens outer ring (thick for depth) */}
        <circle cx="78" cy="62" r="35" stroke="rgba(232,80,10,0.2)" strokeWidth="10" fill="none" />
        {/* Lens main border */}
        <circle cx="78" cy="62" r="35" stroke="#E8500A" strokeWidth="2" fill="rgba(232,80,10,0.05)" />
        {/* Handle */}
        <line x1="105" y1="89" x2="134" y2="120" stroke="#E8500A" strokeWidth="4" strokeLinecap="round" />
        {/* 5-point star inside */}
        <path d="M78 46 L81.5 57.5 L93 57.5 L84 64.5 L87 76 L78 69.5 L69 76 L72 64.5 L63 57.5 L74.5 57.5 Z" fill="#E8500A" />
        {/* Animated scan line */}
        <line x1="47" y1="62" x2="109" y2="62" stroke="rgba(232,80,10,0.8)" strokeWidth="1.5" strokeDasharray="5,4" className="scan-line" />
        {/* Small "candidates" dots outside glass */}
        <circle cx="140" cy="38" r="4" fill="rgba(255,255,255,0.18)" />
        <circle cx="150" cy="55" r="3" fill="rgba(255,255,255,0.12)" />
        <circle cx="148" cy="26" r="3.5" fill="rgba(255,255,255,0.2)" />
        <circle cx="158" cy="42" r="2.5" fill="rgba(255,255,255,0.1)" />
        <circle cx="155" cy="68" r="3" fill="rgba(255,255,255,0.1)" />
      </svg>
    </div>
  );
}
